<template>
  <div class="zt-data-table border rounded">
    <div class="w-full">
      <slot name="header">
        <div class="zt-data-table--header p-6 border-b">
          <h2 v-if="tableName" class="border-none p-0 ">{{ tableName }}</h2>
        </div>
      </slot>
    </div>
    <div class="w-full overflow-x-auto">
      <table class="table-fixed w-full whitespace-no-wrap m-0">
        <thead class="w-full">
          <tr class="zt-data-table--tr text-left text-gray-700 border-none">
            <th
              v-for="column in headers"
              :key="column.dataField"
              :class="{
                'border-none': true,
                'uppercase': column.upper,
                'font-normal': !column.upper,
                'sorting': column.sorted
              }"
              :style="getColumnWidthStyle(column)"
              class="px-6 py-4"
            >
              <div class="w-full">
                <div
                  v-if="column.sortable"
                  class="flex items-center select-none cursor-pointer"
                  @click.prevent="sortByColumn(column)"
                >
                  <div v-if="column.direction" class="mr-4">
                    <zt-icon :name="getSortDirection(column)" />
                  </div>
                  {{ column.name }}
                </div>
                <div v-else class="select-none">
                  {{ column.name }}
                </div>
              </div>
            </th>
          </tr>
        </thead>
        <tbody v-if="loading">
          <tr>
            <td :colspan="columns.length">
              <div
                class="zt-data-loader text-zt-primary-200 flex flex-column justify-center items-center pointer-events-none h-60"
              >
                <div class="zt-data-loader-circle"></div>
                <p v-if="loaderTxt" class="pointer-events-none">
                  {{ loaderTxt }}
                </p>
              </div>
            </td>
          </tr>
        </tbody>
        <tbody v-else>
          <tr
            v-for="(row, rowIndex) in rows"
            :key="rowIndex"
            :class="{
              'zt-data-table-row': true,
              'border-t': rowIndex === 0,
              'border-b': true
            }"
          >
            <td
              v-for="(column, columnIndex) in headers"
              :key="columnIndex"
              :style="getColumnWidthStyle(column)"
              class="px-6 py-4 border-none bg-zt-white"
            >
              <slot :name="column.dataField" :item="row">
                {{ row[column.dataField] }}
              </slot>
            </td>
          </tr>
        </tbody>
        <tfoot v-if="hasPagination">
        <tr>
          <td :colspan="headers.length" class="px-4 py-4 border-none text-xs">
            <div class="flex justify-end items-center">
              <div class="flex flex-none items-center">
                <div class="mr-4">
                  <label class="mr-2">Cantidad por página:</label>
                  <select class="form-select" v-model="perPage" @change="perPageChanged">
                    <option v-for="size in mergedConfig.pagination.perPage" :key="size" :value="size">
                      {{ size }}
                    </option>
                  </select>
                </div>
                <div>
                  {{ (currentPage - 1) * perPage + 1 }} - {{ Math.min(currentPage * perPage, rows.length) }} de {{ rows.length }}
                </div>
              </div>
              <div class="flex flex-none items-center">
                <button
                  class="px-2 py-1 rounded-lg mr-2"
                  :class="{ 'text-zt-gray-400': currentPage === 1 }"
                  :disabled="currentPage === 1"
                  @click="prevPage"
                >
                  <zt-icon :name="mergedConfig.pagination.icons.previous" />
                </button>
                <button
                  class="px-2 py-1 rounded-lg"
                  :class="{ 'text-zt-gray-400': currentPage === totalPages }"
                  :disabled="currentPage === totalPages"
                  @click="nextPage"
                >
                  <zt-icon :name="mergedConfig.pagination.icons.next" />
                </button>
              </div>
            </div>
          </td>
        </tr>
        </tfoot>
      </table>
    </div>
  </div>
</template>

<script>
import { merge } from 'lodash'
const defaultConfig = {
  sort: {
    direction: 'desc',
    dataField: '',
    icons: {
      asc: 'arrow-up',
      desc: 'arrow-down'
    }
  },
  pagination: {
    use: true,
    totals: 0,
    perPage: [ 10, 25, 50 ],
    icons: {
      previous: 'angle-left',
      next: 'angle-right'
    }
  }
}

export default {
  name: 'ztDataTable',
  props: {
    config: {
      type: Object,
      default: () => ({})
    },
    columns: {
      type: Array,
      default: () => ([]),
      require: true
    },
    rows: {
      type: Array,
      default: () => ([]),
      require: true
    },
    tableName: {
      type: String,
      default: ''
    },
    loading:{
      type: Boolean,
      default: false
    }
  },
  computed: {
    hasPagination() {
      return this.mergedConfig.pagination.use === true
    }
  },
  data() {
    return {
      headers: [],
      mergedConfig: merge(defaultConfig, this.config),
      sort: {},
      currentPage: 1,
      perPage: 0,
      totalPages: 20
    }
  },
  created() {
    this.setup()
  },
  methods: {
    toggleSortOrder(sortOrder) {
      return sortOrder === 'desc' ? 'asc' : 'desc';
    },
    sortByColumn(column) {
      column.direction = this.toggleSortOrder(column.direction)
      column.sorted = true
      this.sort = {
        direction: column.direction,
        dataField: column.dataField
      }
      this.headers = this.headers.map(col => {
        if (col.dataField === column.dataField) {
          return column
        }
        return {
          ...col,
          direction: '',
          sorted: false
        }
      })

      this.$emit('sortable-dir', column)
    },
    getColumnWidthStyle(column) {
      return { width: column.width || 'auto' }
    },
    getSortDirection(column) {
      return this.mergedConfig.sort.icons[column.direction]
    },
    setup() {
      this.currentPage = 1;
      this.perPage = this.mergedConfig.pagination.perPage[0];
      this.sort = {
        direction: this.mergedConfig.sort.direction,
        dataField: this.mergedConfig.sort.dataField || this.columns[0].dataField
      }
      this.headers = this.columns.map((column) => {
        const isDefaultSorted = column.dataField === this.sort.dataField;
        const width = column.width
          ? typeof column.width === 'number'
            ? `${column.width}px`
            : column.width
          : 'auto';
        if (column.sortable) {
          column.direction = isDefaultSorted ? this.sort.direction : '';
          column.sorted = false;
        }
        return {
          ...column,
          width
        };
      });
    },
    perPageChanged() {

    },
    prevPage() {

    },
    nextPage() {

    }
  },
  beforeDestroy() {

  }
}
</script>

<style
  src="../../../../styles/components/ztDataTable.css"
  lang="css"
  scoped
></style>